import React from 'react';
import { X } from 'lucide-react';

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

type PanelVariant = 'default' | 'inset' | 'warning' | 'critical';
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

export function Panel({
  className,
  variant = 'default',
  children,
  ...props
}: React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement> & { className?: string; variant?: PanelVariant }>) {
  const variants: Record<PanelVariant, string> = {
    default: 'command-panel bg-cmd-panel border-[rgb(var(--c-border-rgb)/0.95)]',
    inset: 'command-inset bg-cmd-panelMute border-[rgb(var(--c-border-rgb)/0.8)]',
    warning: 'command-panel border-[rgb(var(--c-warning-rgb)/0.38)] bg-[rgb(var(--c-warning-rgb)/0.08)]',
    critical: 'command-panel border-[rgb(var(--c-critical-rgb)/0.38)] bg-[rgb(var(--c-critical-rgb)/0.08)]',
  };

  return <div {...props} className={cx('rounded-[2px]', variants[variant], className)}>{children}</div>;
}

export function TacticalButton({
  className,
  variant = 'primary',
  size = 'md',
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
}) {
  const sizes: Record<ButtonSize, string> = {
    sm: 'px-3 py-1.5 text-[11px]',
    md: 'px-4 py-2 text-xs',
    lg: 'px-5 py-2.5 text-sm',
  };

  const variants: Record<ButtonVariant, string> = {
    primary: 'bg-[color:rgb(var(--c-safe-rgb)/0.12)] border-[color:rgb(var(--c-safe-rgb)/0.38)] text-[color:var(--c-safe-text)] hover:bg-[color:rgb(var(--c-safe-rgb)/0.18)] hover:border-[color:rgb(var(--c-safe-rgb)/0.52)]',
    secondary: 'bg-[color:rgb(var(--c-ink-rgb)/0.03)] border-[color:rgb(var(--c-border-rgb)/0.95)] text-cmd-text hover:bg-[color:rgb(var(--c-ink-rgb)/0.06)] hover:border-[color:rgb(var(--c-text-mute-rgb)/0.22)]',
    ghost: 'bg-transparent border-[color:rgb(var(--c-border-rgb)/0.9)] text-cmd-textMuted hover:bg-[color:rgb(var(--c-ink-rgb)/0.04)] hover:text-cmd-text',
    danger: 'bg-[color:rgb(var(--c-critical-rgb)/0.12)] border-[color:rgb(var(--c-critical-rgb)/0.32)] text-[color:var(--c-critical-text)] hover:bg-[color:rgb(var(--c-critical-rgb)/0.18)]',
  };

  return (
    <button
      {...props}
      className={cx(
        'inline-flex items-center justify-center gap-2 border font-semibold uppercase tracking-[0.12em] transition-colors disabled:opacity-50 disabled:cursor-not-allowed rounded-[2px]',
        sizes[size],
        variants[variant],
        className
      )}
    >
      {children}
    </button>
  );
}

export function TacticalInput(
  props: React.InputHTMLAttributes<HTMLInputElement> & { className?: string }
) {
  // A caller-supplied width (w-20, w-32, …) must win: without this the default w-full stretched short fields (currency,
  // weight %, NAV) across the whole row because both classes were emitted (owner 2026-10-06).
  const hasWidth = /(^|\s)!?(w-|max-w-)/.test(props.className || '');
  return (
    <input
      {...props}
      className={cx(
        hasWidth ? '' : 'w-full',
        'min-w-0 border border-[color:rgb(var(--c-border-rgb)/0.95)] bg-cmd-panelMute px-3 py-2 text-sm text-cmd-text placeholder:text-cmd-textDim focus:border-[color:rgb(var(--c-active-rgb)/0.45)] focus:outline-none rounded-[2px]',
        props.className
      )}
    />
  );
}

export function TacticalSelect(
  props: React.SelectHTMLAttributes<HTMLSelectElement> & { className?: string }
) {
  return (
    <select
      {...props}
      className={cx(
        'w-full min-w-0 border border-[color:rgb(var(--c-border-rgb)/0.95)] bg-cmd-panelMute px-3 py-2 text-sm text-cmd-text focus:border-[color:rgb(var(--c-active-rgb)/0.45)] focus:outline-none rounded-[2px]',
        props.className
      )}
    />
  );
}

export function StatusTag({
  tone = 'default',
  className,
  children,
}: React.PropsWithChildren<{ tone?: 'default' | 'safe' | 'warning' | 'critical' | 'active'; className?: string }>) {
  const toneClass = tone === 'default' ? '' : tone;
  return <span className={cx('status-tag', toneClass, className)}>{children}</span>;
}

export function PageHeader({
  kicker,
  title,
  description,
  icon,
  actions,
}: {
  kicker?: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className="page-header">
      <div className="min-w-0 flex-1">
        {kicker && <div className="page-kicker mb-2">{kicker}</div>}
        <div className="flex items-start gap-3">
          {icon && <div className="mt-0.5 text-cmd-textMuted shrink-0">{icon}</div>}
          <div className="min-w-0">
            <h1 className="command-title text-xl md:text-2xl">{title}</h1>
            {description && <p className="mt-2 max-w-4xl text-sm text-cmd-textMuted leading-relaxed">{description}</p>}
          </div>
        </div>
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </div>
  );
}

export function ChartFrame({
  title,
  subtitle,
  actions,
  children,
  className,
}: React.PropsWithChildren<{ title: string; subtitle?: string; actions?: React.ReactNode; className?: string }>) {
  return (
    <Panel className={cx('chart-frame overflow-hidden', className)}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="section-heading mb-1">Visualization</div>
          <h3 className="command-title text-sm">{title}</h3>
          {subtitle && <p className="mt-1 text-xs text-cmd-textDim">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </Panel>
  );
}

export function ModalShell({
  title,
  subtitle,
  onClose,
  actions,
  children,
  size = 'xl',
}: React.PropsWithChildren<{
  title: string;
  subtitle?: string;
  onClose?: () => void;
  actions?: React.ReactNode;
  size?: 'lg' | 'xl' | 'full';
}>) {
  const widths: Record<'lg' | 'xl' | 'full', string> = {
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
    full: 'max-w-[1600px]',
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-[2px] flex items-center justify-center p-4" onClick={onClose}>
      <Panel
        className={cx(
          'w-full max-h-[90vh] overflow-hidden flex flex-col rounded-[2px]',
          widths[size]
        )}
      >
        <div className="shrink-0 border-b border-[color:rgb(var(--c-border-rgb)/0.95)] bg-cmd-panelAlt px-5 py-3 flex items-start justify-between gap-4">
          <div>
            <div className="section-heading mb-1">Dossier View</div>
            <h2 className="command-title text-lg">{title}</h2>
            {subtitle && <p className="mt-1 text-xs text-cmd-textDim">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {actions}
            {onClose && (
              <TacticalButton variant="ghost" size="sm" onClick={onClose} aria-label="Close">
                <X className="h-4 w-4" />
              </TacticalButton>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto bg-cmd-surface px-5 py-4" onClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      </Panel>
    </div>
  );
}

export function ControlBar({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) {
  return <div className={cx('control-bar', className)}>{children}</div>;
}

export function TableShell({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <Panel className={cx('table-shell overflow-hidden', className)}>
      <div className="overflow-x-auto">{children}</div>
    </Panel>
  );
}
